import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type LayoutChangeEvent, View } from "react-native";
import { useAppTheme } from "../../../useAppTheme";
import {
	SEEK_BAR_METRICS,
	type SeekBarProps,
	type SeekBarVariant,
	activeSegmentWidth,
	clamp,
	inactiveSegmentWidth,
	seekBarStyles,
} from "./styles";

export type { SeekBarProps, SeekBarVariant };

const SeekBar = ({ position, duration, onSeek, variant }: SeekBarProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const metrics = SEEK_BAR_METRICS[variant];
	const [trackWidth, setTrackWidth] = useState(0);
	const [dragX, setDragX] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const containerRef = useRef<View>(null);
	const containerLeftRef = useRef(0);

	const outerRadius =
		variant === "full" ? theme.roundness * 4 : theme.roundness;
	const innerRadius = theme.roundness / 2;
	const halfHandleWidth = metrics.handleWidth / 2;

	const handleLayout = useCallback((event: LayoutChangeEvent) => {
		setTrackWidth(event.nativeEvent.layout.width);
	}, []);

	useEffect(() => {
		if (!isDragging) {
			const progress = duration > 0 ? position / duration : 0;
			setDragX(clamp(progress * trackWidth, 0, trackWidth));
		}
	}, [duration, isDragging, position, trackWidth]);

	const updateDragPosition = useCallback(
		(x: number) => {
			const nextX = clamp(x, 0, trackWidth);
			setDragX(nextX);
			return nextX;
		},
		[trackWidth],
	);

	const commitSeek = useCallback(
		(nextX: number) => {
			const nextProgress = trackWidth > 0 ? nextX / trackWidth : 0;
			onSeek(nextProgress * duration);
		},
		[duration, onSeek, trackWidth],
	);

	const measureContainer = useCallback(() => {
		const node = containerRef.current as unknown as HTMLElement;
		if (node?.getBoundingClientRect) {
			containerLeftRef.current = node.getBoundingClientRect().left;
		}
	}, []);

	const getRelativeX = useCallback(
		(pageX: number) => pageX - containerLeftRef.current,
		[],
	);

	return (
		<View
			ref={containerRef}
			onLayout={handleLayout}
			style={[
				seekBarStyles.container,
				{ height: metrics.containerHeight, cursor: "pointer" },
			]}
			accessibilityRole="adjustable"
			accessibilityLabel={t("podcast.a11y.seekBar")}
			accessibilityValue={{
				min: 0,
				max: Math.floor(duration),
				now: Math.floor(position),
			}}
			onStartShouldSetResponder={() => true}
			onMoveShouldSetResponder={() => true}
			onResponderGrant={(event) => {
				setIsDragging(true);
				measureContainer();
				updateDragPosition(getRelativeX(event.nativeEvent.pageX));
			}}
			onResponderMove={(event) => {
				updateDragPosition(getRelativeX(event.nativeEvent.pageX));
			}}
			onResponderRelease={(event) => {
				const nextX = updateDragPosition(getRelativeX(event.nativeEvent.pageX));
				setIsDragging(false);
				commitSeek(nextX);
			}}
			onResponderTerminate={() => {
				setIsDragging(false);
			}}
		>
			<View
				style={[
					seekBarStyles.activeSegment,
					{
						width: activeSegmentWidth(dragX, metrics.handleGap),
						height: metrics.trackHeight,
						backgroundColor: theme.colors.primary,
						borderTopLeftRadius: outerRadius,
						borderBottomLeftRadius: outerRadius,
						borderTopRightRadius: innerRadius,
						borderBottomRightRadius: innerRadius,
					},
				]}
			/>
			<View
				style={[
					seekBarStyles.inactiveSegment,
					{
						width: inactiveSegmentWidth(dragX, trackWidth, metrics.handleGap),
						height: metrics.trackHeight,
						backgroundColor: theme.colors.secondaryContainer,
						borderTopLeftRadius: innerRadius,
						borderBottomLeftRadius: innerRadius,
						borderTopRightRadius: outerRadius,
						borderBottomRightRadius: outerRadius,
					},
				]}
			/>
			<View
				style={[
					seekBarStyles.handle,
					{
						width: metrics.handleWidth,
						height: metrics.handleHeight,
						borderRadius: theme.roundness / 2,
						backgroundColor: theme.colors.primary,
						transform: [
							{
								translateX: clamp(
									dragX - halfHandleWidth,
									-halfHandleWidth,
									Math.max(trackWidth - halfHandleWidth, -halfHandleWidth),
								),
							},
						],
					},
				]}
			/>
		</View>
	);
};

export default SeekBar;
